import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

let gsapRegistered = false;
function ensureGSAP() {
    if (gsapRegistered) return;
    gsap.registerPlugin(ScrollTrigger);
    gsapRegistered = true;
}

/**
 * 공용 스크롤러 가져오기 (너의 패턴 그대로)
 */
export function getScroller(rootRef) {
    return (
        rootRef?.current ||
        document.querySelector(".homepage_wrapper") ||
        window
    );
}

/**
 * Lenis + ScrollTrigger 세팅을 한 번에.
 * - scroller: getScroller(rootRef) 결과
 * - contentEl: scroller 안의 .root 또는 첫번째 엘리먼트(없으면 scroller 자체)
 * - wrap: 컴포넌트 루트(정리를 위해 필요)
 *
 * 반환: { scroller, createdLenis, cleanup }
 */
export function setupLenyStyle({ wrap, scroller, contentEl }) {
    ensureGSAP();
    if (!wrap) return { scroller, createdLenis: false, cleanup: () => {} };

    let lenis;
    let createdLenis = false;

    // Lenis 생성 (엘리먼트 스크롤러만; window일 때는 생성 X)
    if (scroller && scroller !== window && !scroller.dataset.lenisInitialized) {
        lenis = new Lenis({
            wrapper: scroller,
            content: contentEl,
            smoothWheel: true,
            normalizeWheel: true,
            wheelMultiplier: 1.05,
            gestureOrientation: "vertical",
            lerp: 0.12,
        });
        createdLenis = true;
        scroller.dataset.lenisInitialized = "true";

        // GSAP ticker에 Lenis 연결
        const ticker = (t) => lenis.raf(t * 1000);
        lenis.on("scroll", ScrollTrigger.update);
        gsap.ticker.add(ticker);

        // ST 프록시
        ScrollTrigger.scrollerProxy(scroller, {
            scrollTop(value) {
                if (arguments.length) lenis.scrollTo(value, { immediate: true });
                return lenis.scroll;
            },
            getBoundingClientRect() {
                return {
                    top: 0,
                    left: 0,
                    width: window.innerWidth,
                    height: window.innerHeight,
                };
            },
        });

        // cleanup 핸들 저장
        wrap.__cleanupLenis = () => {
            gsap.ticker.remove(ticker);
            lenis && lenis.destroy();
            delete scroller.dataset.lenisInitialized;
        };
    } else if (scroller !== window) {
        // 이미 Lenis 초기화된 스크롤러 → 기본 프록시만 보장
        ScrollTrigger.scrollerProxy(scroller, {
            scrollTop(value) {
                if (arguments.length) scroller.scrollTop = value;
                return scroller.scrollTop || 0;
            },
            getBoundingClientRect() {
                return {
                    top: 0,
                    left: 0,
                    width: window.innerWidth,
                    height: window.innerHeight,
                };
            },
        });
    }

    // ST 기본값(한 번 더 안전하게 주입)
    ScrollTrigger.defaults({
        scroller: scroller !== window ? scroller : undefined,
        markers: false,
        invalidateOnRefresh: true,
        pinType:
            scroller !== window &&
            getComputedStyle(scroller).transform !== "none"
                ? "transform"
                : "fixed",
    });

    const cleanup = () => {
        if (createdLenis) wrap.__cleanupLenis && wrap.__cleanupLenis();
    };

    return { scroller, createdLenis, cleanup };
}

/**
 * 이미지/폰트 로드 후 ScrollTrigger.refresh() 도우미
 * - 컴포넌트 useEffect에서 호출하면 좌표 안정화됨
 */
export function refreshOnAssetsReady(wrap, cb) {
    const doRefresh = () => {
        cb && cb();
        ScrollTrigger.refresh();
    };

    const onResize = () => doRefresh();
    window.addEventListener("resize", onResize);

    const imgs = Array.from(wrap.querySelectorAll("img"));
    const fontReady =
        "fonts" in document
            ? document.fonts.ready.catch(() => {})
            : Promise.resolve();

    Promise.allSettled([
        fontReady,
        ...imgs.map((img) => img.decode?.().catch(() => {}) || Promise.resolve()),
    ]).finally(doRefresh);

    return () => {
        window.removeEventListener("resize", onResize);
    };
}
